import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useLocation } from 'wouter'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, AlertTriangle } from 'lucide-react'
import { sanitizeHtml } from '@/lib/sanitize'
import type { TermsAndConditions } from '@shared/schema'

export default function TermsPage() {
  const [, navigate] = useLocation()

  // Fetch active terms and conditions from the database
  const { data: activeTerms, isLoading, error } = useQuery<TermsAndConditions | null>({
    queryKey: ['/api/terms-and-conditions/active'],
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  })

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="mb-6"
            data-testid="button-back-home"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>

          <Card>
            <CardHeader>
              {isLoading ? (
                <>
                  <Skeleton className="h-8 w-3/4 mx-auto mb-2" data-testid="skeleton-title" />
                  <Skeleton className="h-4 w-1/2 mx-auto" data-testid="skeleton-date" />
                </>
              ) : (
                <>
                  <CardTitle className="text-3xl font-bold text-center" data-testid="text-title">
                    {activeTerms?.title || 'Terms and Conditions'}
                  </CardTitle>
                  <p className="text-muted-foreground text-center" data-testid="text-last-updated">
                    Last updated: {activeTerms?.updatedAt 
                      ? new Date(activeTerms.updatedAt).toLocaleDateString() 
                      : 'Unknown'
                    }
                  </p>
                  {activeTerms?.version && (
                    <p className="text-sm text-muted-foreground text-center" data-testid="text-version">
                      Version: {activeTerms.version}
                    </p>
                  )}
                </>
              )}
            </CardHeader>
            <CardContent className="prose max-w-none space-y-6">
              {isLoading ? (
                <div className="space-y-4" data-testid="skeleton-content">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-4/5" />
                  </div>
                </div>
              ) : error ? (
                <Alert variant="destructive" data-testid="alert-error">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Unable to load Terms and Conditions</strong>
                    <br />
                    We're having trouble loading the current terms and conditions. Please try refreshing the page or contact our support team.
                    <div className="mt-3 space-y-1">
                      <p><strong>Email:</strong> support@prizedrop.co.za</p>
                      <p><strong>Website:</strong> www.prizedrop.co.za</p>
                    </div>
                  </AlertDescription>
                </Alert>
              ) : !activeTerms ? (
                <Alert data-testid="alert-no-terms">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>No Active Terms and Conditions</strong>
                    <br />
                    There are currently no active terms and conditions available. Please contact our support team for assistance.
                    <div className="mt-3 space-y-1">
                      <p><strong>Email:</strong> support@prizedrop.co.za</p>
                      <p><strong>Website:</strong> www.prizedrop.co.za</p>
                    </div>
                  </AlertDescription>
                </Alert>
              ) : (
                <div 
                  className="prose max-w-none space-y-6"
                  data-testid="content-terms"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(activeTerms.content) }}
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}